import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Autocomplete, Box, Button, CircularProgress, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { resolveProjectTicket, scheduleProjectTicket } from '../../../Redux/actions';
import { THEME_COLOR, buttonStyles, datePickerSlotProps } from '../../../theme/formStyles';
import { userLabel } from '../editor/editorOptions';

const fieldSx = { '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME_COLOR }, '& .MuiInputLabel-root.Mui-focused': { color: THEME_COLOR } };
const sameUser = (a, b) => !!a && !!b && String(a).toLowerCase() === String(b).toLowerCase();

// Assign / schedule (admins) and resolve (admins or the assigned engineer) for client and mobile tickets.
// Needs a LocalizationProvider above it.
export default function TicketActions({ ticket, canManage, currentUserId, greenUsers, onDone }) {
    const dispatch = useDispatch();
    const [mode, setMode] = useState(null); // 'assign' | 'resolve'
    const [engineer, setEngineer] = useState(() => greenUsers.find(u => sameUser(u.user_id, ticket.engineer?.id)) || null);
    const [date, setDate] = useState(ticket.scheduled_date ? dayjs(ticket.scheduled_date) : null);
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    if (!ticket.assignable || ticket.stage === 'resolved') return null;
    const isEngineer = sameUser(ticket.engineer?.id, currentUserId);
    const canResolve = canManage || isEngineer;
    if (!canManage && !canResolve) return null;

    const run = (action, message) => {
        setBusy(true); setError(null);
        dispatch(action).then((res) => {
            setBusy(false);
            if (res?.data?.success) { setMode(null); onDone(res.data.message || message); }
            else setError(res?.data?.message || 'Something went wrong');
        });
    };
    const assign = () => {
        if (!engineer) { setError('Pick the engineer'); return; }
        run(scheduleProjectTicket(ticket.id, { engineer: { id: engineer.user_id, name: engineer.name }, scheduled_date: date && date.isValid() ? date.format('YYYY-MM-DD') : null }), 'Saved');
    };

    return (
        <Box sx={{ mt: 1.25, pt: 1.25, borderTop: '1px dashed #e5e9ee' }}>
            {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
            {mode === 'assign' && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 170px auto' }, gap: 1, alignItems: 'start' }}>
                    <Autocomplete size="small" options={greenUsers} getOptionLabel={userLabel} isOptionEqualToValue={(o, v) => o.user_id === v.user_id}
                        value={engineer} onChange={(e, v) => setEngineer(v)} renderInput={(params) => <TextField {...params} label="Engineer *" sx={fieldSx} />} />
                    <DatePicker label="Visit date" format="DD/MM/YYYY" value={date} minDate={dayjs()} onChange={setDate}
                        slotProps={datePickerSlotProps({ size: 'small', fullWidth: true })} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="contained" onClick={assign} disabled={busy} sx={buttonStyles.contained}>{busy ? <CircularProgress size={18} color="inherit" /> : 'Save'}</Button>
                        <Button onClick={() => setMode(null)} disabled={busy} sx={{ color: '#6b7785', textTransform: 'none' }}>Cancel</Button>
                    </Box>
                </Box>
            )}
            {mode === 'resolve' && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <TextField size="small" label="What was done (optional)" value={note} onChange={e => setNote(e.target.value)} sx={{ ...fieldSx, flex: '1 1 260px' }} inputProps={{ maxLength: 1000 }} />
                    <Button variant="contained" onClick={() => run(resolveProjectTicket(ticket.id, note.trim()), 'Ticket resolved')} disabled={busy}
                        sx={{ ...buttonStyles.contained, bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}>{busy ? <CircularProgress size={18} color="inherit" /> : '✓ Mark resolved'}</Button>
                    <Button onClick={() => setMode(null)} disabled={busy} sx={{ color: '#6b7785', textTransform: 'none' }}>Cancel</Button>
                </Box>
            )}
            {!mode && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {canManage && <Button size="small" variant="outlined" onClick={() => setMode('assign')} sx={buttonStyles.outlined}>{ticket.engineer ? '✏️ Change engineer / date' : '👷 Assign engineer'}</Button>}
                    {canResolve && <Button size="small" variant="outlined" onClick={() => setMode('resolve')} sx={buttonStyles.outlined}>✓ Resolve</Button>}
                </Box>
            )}
        </Box>
    );
}
